const $$splitNotFoundComponentImporter = () => import('/home/runner/work/nexus/nexus/apps/frontend/src/routes/(app)/route.tsx?tsr-split=notFoundComponent');
const $$splitComponentImporter = () => import('/home/runner/work/nexus/nexus/apps/frontend/src/routes/(app)/route.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { ldContextFactory } from '@/app';
import { setLogoutHandler, setTokenProvider } from '@/common/api/tempoApi';
import { FullSizeSpinner } from '@/common/components/ui/FullSizeSpinner';
import { authSessionQueryKey, EMPTY_SESSION } from '@/common/hooks/useAuthSession';
import { permissionsQueryOptions } from '@/common/queries/access/permissionsQueryOptions';
import { goToLogout } from '@/common/utils/authRedirect';
import { fetchAccessToken, SessionEndedError } from '@/common/utils/fetchAccessToken';
import { getJwtUserContext } from '@/common/utils/getJwtUserContext';
export const Route = createFileRoute('/(app)')({
  component: lazyRouteComponent($$splitComponentImporter, 'component'),
  pendingComponent: FullSizeSpinner,
  notFoundComponent: lazyRouteComponent($$splitNotFoundComponentImporter, 'notFoundComponent'),
  beforeLoad: async ({
    context
  }) => {
    const {
      auth,
      flagOverrides
    } = context;
    if (!auth) {
      throw redirect({
        to: '/'
      });
    }
    if (!auth.isLoading && !auth.isAuthenticated) {
      const hasPath = window.location.pathname !== '/';
      throw redirect({
        to: '/login',
        search: hasPath ? {
          returnTo: window.location.href
        } : {}
      });
    }
    let flags = {} as FlagKeys;
    setLogoutHandler(() => goToLogout(window.location.origin));
    try {
      const accessToken = await fetchAccessToken();
      // The provider must throw, not swallow: tempoApi only logs out on a SessionEndedError.
      setTokenProvider(fetchAccessToken, accessToken);
      const authContext = getJwtUserContext(accessToken);
      const {
        userId,
        organizationId,
        organizationName
      } = authContext;
      if (context.ldClient) {
        // identify needs both keys (an empty key throws "Invalid context"); the anonymous
        // flags + overrides below still apply when the token is incomplete.
        if (userId && organizationId) {
          const ldCtx = ldContextFactory.multiContext({
            organizationInput: {
              organizationId,
              organizationName
            },
            userInput: {
              userId
            }
          });
          const currentCtx = context.ldClient.getContext();
          const multiCtx = 'kind' in currentCtx && currentCtx.kind === 'multi' ? currentCtx as LDMultiKindContext : null;
          const contextChanged = !multiCtx || (multiCtx.user as LDContextCommon | undefined)?.key !== userId || (multiCtx.organization as LDContextCommon | undefined)?.key !== organizationId;
          if (contextChanged) {
            try {
              await context.ldClient.identify(ldCtx);
            } catch {
              // A LaunchDarkly identify failure must not break auth/routing.
            }
          }
        }
        const allFlags = context.ldClient.allFlags();
        flags = {
          ...allFlags,
          ...flagOverrides
        } as FlagKeys;
      }
      let permissions: Partial<PermissionsResponseDTO>;
      try {
        permissions = await context.queryClient.fetchQuery(permissionsQueryOptions);
      } catch {
        permissions = {} as Partial<PermissionsResponseDTO>;
      }
      return {
        flags,
        accessToken,
        permissions,
        ...authContext
      };
    } catch (error) {
      if (error instanceof SessionEndedError) {
        // The cached /auth/me answer still says authenticated; /login would bounce straight back.
        context.queryClient.setQueryData(authSessionQueryKey(), EMPTY_SESSION);
        const hasPath = window.location.pathname !== '/';
        throw redirect({
          to: '/login',
          search: hasPath ? {
            returnTo: window.location.href
          } : {}
        });
      }
      return {
        flags,
        accessToken: undefined,
        email: undefined,
        subscription: undefined,
        userId: undefined,
        organizationId: undefined,
        auth0OrganizationId: undefined,
        organizationName: undefined,
        connectionName: undefined,
        authError: true,
        permissions: {} as Partial<PermissionsResponseDTO>
      };
    }
  }
});